<template>
    <div>
        <!-- Show form if no accepted offers -->
        <form v-if="!acceptedOffers && isActiveProduct" class="card-body mobile-sticky-form ml-1 mr-1 form-bg"
            @submit.prevent="handleSubmit">
            <!-- Buy It Now Section -->
            <div class="buy-now-section" v-if="hasBuyItNow">
                <div class="col-12 col-md-4 offset-md-1'" v-if="canBuy">
                    <button type="button" @click="confirmBuyItNow" class="btn btn-site text-center btn-buy-now w-100">
                        {{ $t('ecommerce.product.buy-it-now') }}
                    </button>
                </div>
            </div>

            <div class="sprtr mt-1" v-if="hasBuyItNow"></div>

            <!-- Auction or Fixed Price Form -->
            <div class="row mt-1">
                <!-- Auction -->
                <div class="col-12" v-if="isAuction">
                    <!-- Quantity -->
                    <div v-if="product.quantity > 1 && canBid">
                        <label class="" for="quantity">
                            {{ $t('ecommerce.product.quantity') }} *
                        </label>
                        <input id="quantity" v-model.number="localQuantity" type="number" :max="product.quantity"
                            min="1" class="form-control mt-1" />
                    </div>
                </div>

                <!-- Fixed Price -->
                <div class="row col-12" v-else>
                    <div class="form-group row align-items-center no-gutters w-100" v-if="product.quantity > 1">
                        <label for="quantity" class="col-4 col-sm-4 col-md-3 col-form-label mb-0">
                            {{ $t('ecommerce.product.quantity') }} *
                        </label>
                        <div class="col-8 col-sm-8 col-md-9">
                            <input id="quantity" v-model.number="localQuantity" type="number" :max="product.quantity"
                                min="1" class="form-control" />
                        </div>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="row d-flex mt-1">
                <div class="col-12" v-if="canAct">
                    <!-- Auction Buttons -->
                    <div v-if="isAuction" class="d-flex justify-content-between gap-2 flex-row">
                        <button type="button" :disabled="!canBid" class="btn btn-site text-center w-100"
                            :class="hasUserBids ? (authCurrentBid == currentBid ? 'btn-success' : 'btn-danger') : 'btn-primary'"
                            @click="$emit('make-auction')">
                            {{ $t(hasUserBids ? 'action.increaseBid' : 'action.placeBid') }}
                            {{ localPrice | currency('DA', 0, {
                                symbolOnLeft: false
                            }) }}
                        </button>
                    </div>

                    <!-- Fixed Price Buttons -->
                    <div v-else class="d-flex justify-content-between gap-2 flex-row">
                        <button v-if="product.accept_offer && canAct" type="button" @click="$emit('make-offer')"
                            class="btn btn-make-offer">
                            {{ $t('action.makeAnOffer') }}
                        </button>
                        <button type="button" @click="confirmBuyItNow"
                            class="btn btn-buy-now">
                            {{ $t('action.buyNow') }}
                        </button>
                    </div>

                </div>
            </div>
        </form>

        <!-- Show accepted offer card if exists -->
        <div v-else-if="acceptedOffers" class="card-body mobile-sticky-form ml-1 mr-1 form-bg row">
            <button disabled class="col-6 offer-status offer-status-accepted">
                <i class="mdi mdi-check-circle"></i>
                {{ $t("ecommerce.product.acceptedOffers") }}
            </button>
            <button disabled class="col-6 offer-status offer-status-accepted" v-if="product.orders.length >0">
                <i class="mdi mdi-check-circle"></i>
                {{ $t("sentence.sold") }}
            </button>
            <router-link v-else class="col-6 offer-status  btn-offer-checkout"
                :to="{ name: 'checkoutOrder', params: { id: acceptedOffers.id, type: 'offer' } }">
                {{ $t("page.checkout") }}
            </router-link>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ProductActionSection',
    props: {
        product: {
            type: Object,
            required: true,
        },
        acceptedOffers: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            required: true
        },
        isMobile: {
            type: Boolean,
            default: false,
        },
    },
    emits: [
        'place-bid',
        'buy-now',
        'buy-fixed',
        'make-offer',
        'show-auction-history',
        'make-auction',
    ],
    data() {
        return {
            localPrice: '',
            localQuantity: 1,
            bidError: '',
        };
    },
    computed: {
        isAuction() {
            return this.product.sales_type==='auction';
        },
        hasBuyItNow() {
            return false||this.product.buy_it_now_price!==null;
        },
        hasBids() {
            return this.product.bids&&this.product.bids.length>0;
        },
        currentBid() {
            return this.hasBids? this.product.bids[0].price:this.product.starting_price;
        },
        authCurrentBid() {
            const product=this.product;
            const authId=this.$store.state.user?.id;

            if (!product||!authId) return 0;

            const bids=Array.isArray(product.bids)? product.bids:[];

            // Filter bids belonging to the authenticated user
            const userBids=bids.filter(b => b.user_id===authId);

            if (userBids.length===0) {
                return product.starting_price??0;
            }

            // Sort user bids by price DESC
            const highestUserBid=userBids.sort((a, b) => b.price-a.price)[0];

            return highestUserBid.price??product.starting_price??0;
        },
        minimumBid() {
            return Math.ceil(this.currentBid)+Math.ceil(this.bidIncrement);
        },
        bidIncrement() {
            const price=this.currentBid;

            if (price<100) return 5;
            if (price<1000) return 10;
            if (price<2000) return 50;
            if (price<10000) return 100;
            return Math.ceil(price*0.05);
        },
        bidStep() {
            // Same logic as bidIncrement, used for input step
            return this.bidIncrement;
        },
        quickBidPrice() {
            return this.minimumBid;
        },
        canBid() {
            return this.product.time_left>0&&this.product.has_been_sold===0;
        },
        isActiveProduct() {
            return this.product.sales_type==='fixed'&&this.product.has_been_sold===0||this.product.sales_type==='auction'&&this.product.time_left>0&&this.product.has_been_sold===0;
        },
        canBuy() {
            return this.canBid||this.product.has_been_sold===0;
        },
        canAct() {
            return this.product.has_been_sold===0;
        },
        isBidValid() {
            if (!this.isAuction||!this.canBid) return false;
            const bid=parseFloat(this.localPrice);
            return !isNaN(bid)&&bid>=this.minimumBid&&bid>this.currentBid;
        },
        hasUserBids() {
            const authId=this.$store.state.user?.id;
            if (!authId||!this.product) return false;

            const bids=Array.isArray(this.product.bids)? this.product.bids:[];
            return bids.some(b => b.user_id===authId);
        },
    },
    watch: {
        product: {
            immediate: true,
            handler(newVal) {
                if (newVal) {
                    this.localQuantity=1;
                    this.localPrice='';
                    this.bidError='';
                }
            },
        },
        localQuantity(val) {
            if (val>this.product.quantity) {
                this.localQuantity=this.product.quantity;
            }
            if (val<1) this.localQuantity=1;
        },
    },
    methods: {
        validateBid() {
            const bid=parseFloat(this.localPrice);
            if (isNaN(bid)) {
                this.bidError=this.$t('ecommerce.product.enterValidBid');
            } else if (bid<=this.currentBid) {
                this.bidError=`${this.$t('ecommerce.product.bidMustExceed')} ${this.currentBid} DA`;
            } else if (bid<this.minimumBid) {
                this.bidError=`${this.$t('ecommerce.product.minimumBid')} ${this.minimumBid} DA`;
            } else {
                this.bidError='';
            }
        },

        setQuickBid() {
            this.localPrice=this.quickBidPrice;
            this.validateBid();
        },
        confirmBuyItNow() {
            this.$emit('buy-now', {
                productId: this.product.id,
                quantity: this.product.quantity>1? this.localQuantity:1,
            });
        },
        confirmBuyFixed() {
            this.$emit('buy-fixed', {
                productId: this.product.id,
                quantity: this.localQuantity,
            });
        },
        showAuctionModal() {
            // Ask parent component to show the auction modal (modal lives in ProductManagement)
            this.$emit('show-auction-modal');
        },
        handleSubmit() {
            // Prevent default form submission
        }
    },
};
</script>

<style scoped>
.form-bg {
    background: var(--color-e);
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.buy-now-section {
    margin-top: 4%;
    display: flex;
    align-items: center;
    gap: 15px;
}

.auction-condition {
    font-size: 15px;
    font-weight: 600;
    color: var(--color-3);
    margin: 0;
}

.price-number {
    font-size: 24px;
    font-weight: bold;
    color: var(--color-secondary);
    margin-left: 8px;
    display: flex;
    align-items: flex-end;
}

.sprtr {
    height: 1px;
    background: linear-gradient(90deg, transparent, #ddd, transparent);
    margin: 15px 0;
}

label {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-5);
    display: block;
    margin-bottom: 0px;
}

.btn-buy-fixed {
    background: linear-gradient(135deg, var(--color-oo) 0%, var(--color-oo-200) 100%);
    color: white !important;
    margin-top: 0px;
}

.btn-buy-fixed:hover {
    background: linear-gradient(135deg, var(--color-oo-200) 0%, var(--color-oo-300) 100%);
}

.action-buttons {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: stretch;
    justify-content: space-between;
}

.action-buttons .btn {
    flex: 1 1 30%;
    height: 52px;
    display: flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    text-align: center;
    padding: 0 16px;
}

.fixed-inline-group {
    display: flex;
    align-items: center;
    width: 100%;
    gap: 16px;
}

.fixed-inline-group label,
.fixed-inline-group .price-number,
.fixed-inline-group .form-control {
    margin-bottom: 0;
}

.fixed-inline-group .form-control {
    min-width: 60px;
    height: 40px;
    font-size: 15px;
}

/* Offer Card Styles */
.offer-card {
    display: flex;
    margin-bottom: 1rem;
}


.status-icon {
    width: 20px;
    height: 20px;
}

/* Mobile Styles */
@media (max-width: 767px) and (orientation: landscape) {
    .form-bg {
        padding: 10px 14px;
    }

    .btn {
        min-height: 42px;
    }

    .price-number {
        font-size: 16px;
    }
}

@media (max-width: 767px) {
    .mobile-sticky-form {
        position: fixed;
        bottom: 65px;
        left: 0;
        right: 0;
        z-index: 50;
        background: white;
        border-top-left-radius: 16px;
        border-top-right-radius: 16px;
        box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
        padding: 16px;
    }

    .fixed-inline-group {
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        gap: 8px;
    }

    .fixed-inline-group .form-control {
        width: 100%;
        min-width: 0;
        max-width: none;
    }

    .form-bg {
        padding: 12px;
        border-radius: 10px;
        margin: 0 -6px;
    }

    .buy-now-section {
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        margin-top: 0;
    }

    .auction-condition {
        font-size: 13px;
        text-align: center;
    }

    .price-number {
        display: flex;
        align-items: flex-end;
        margin-top: 4px;
        margin-bottom: 4px;
        text-align: center;
        word-break: break-word;
    }

    .sprtr {
        margin: 10px 0;
    }

    label {
        font-size: 12px;
        margin-bottom: 4px;
    }

    .form-control {
        padding: 10px 12px;
        font-size: 14px;
        border-radius: 8px;
    }

    .btn {
        padding: 12px 14px;
        font-size: 13px;
        border-radius: 8px;
        font-weight: 600;
        width: 100%;
    }

    .btn-buy-now,
    .btn-buy-fixed {
        margin-top: 0px;
        min-height: 46px;
        font-size: 13px;
    }

    .row {
        margin-left: -4px;
        margin-right: -4px;
    }

    .row>div {
        padding-left: 4px;
        padding-right: 4px;
    }

    .fixed-inline-group {
        flex-direction: column;
        align-items: stretch;
        width: 100%;
        gap: 6px;
    }

    .fixed-inline-group label,
    .fixed-inline-group {
        font-size: 13px;
    }

    .fixed-inline-group .form-control {
        width: 100%;
        font-size: 14px;
    }

    .d-flex {
        gap: 8px;
    }

    .action-buttons {
        flex-direction: column;
        gap: 8px;
    }

    .action-buttons .btn {
        width: 100%;
        height: 46px;
    }

    /* Offer card mobile positioning */
    .offer-card {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        width: 100%;
        background: white;
        padding: 12px 16px;
        box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.12);
        z-index: 103;
    }
}

/* Extra small phones */
@media (max-width: 360px) {
    .form-bg {
        padding: 10px;
    }

    .price-number {
        font-size: 16px;
    }

    .btn {
        font-size: 12px;
    }

    label {
        font-size: 11px;
    }
}
</style>